import { clsx } from 'clsx'
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideImage,
  LucideX,
} from 'lucide-react'
import { MouseEvent, useMemo, useState } from 'react'
import Lightbox, { ImagesListType } from 'react-spring-lightbox'
import { WatermarkedImageDisplay } from '~/components/app/listing/WatermarkedImage'
import { Button } from '~/components/ui'
import { useEmbed } from '~/hooks'
import { type ListingImage } from '~/models'
import { cn, isWatermarkedImage } from '~/utils'

type GalleryProps = {
  className?: string
  images?: ListingImage[]
  children?: React.ReactNode
}

export default function ListingGallery({
  className,
  images,
  children,
}: GalleryProps) {
  const { isEmbed } = useEmbed()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isGalleryFullscreen, setIsGalleryFullscreen] = useState(false)

  const imagesList: ImagesListType = useMemo(() => {
    return (images || []).map((image) => {
      return {
        src: image.url,
        loading: 'auto',
        alt: '',
      }
    })
  }, [images])

  const renderCloseButton = () => {
    return (
      <Button
        variant="map"
        size="map"
        className={clsx(
          `absolute right-2 top-2 z-10 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75`,
          !isGalleryFullscreen && 'hidden'
        )}
        onClick={() => {
          setIsGalleryFullscreen(false)
        }}>
        <LucideX className="h-6 w-6" />
      </Button>
    )
  }

  const gotoPrevious = () =>
    currentImageIndex > 0 && setCurrentImageIndex(currentImageIndex - 1)

  const gotoNext = () =>
    currentImageIndex + 1 < imagesList.length &&
    setCurrentImageIndex(currentImageIndex + 1)

  const updateIndex = (e: MouseEvent<any> | null, direction: number) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    let newIndex = currentImageIndex + direction
    if (newIndex < 0) newIndex = (images || []).length - 1
    if (newIndex >= (images || []).length) newIndex = 0
    setCurrentImageIndex(newIndex)
  }

  const renderPrevButton = () => {
    if ((images || []).length >= 2) {
      return (
        <Button
          variant="map"
          size="map"
          className="absolute left-2 top-1/2 z-10 -translate-y-1/2 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75 print:hidden"
          onClick={(e) => updateIndex(e, -1)}>
          <LucideChevronLeft className="h-5 w-5" />
        </Button>
      )
    }
  }

  const renderNextButton = () => {
    if ((images || []).length >= 2) {
      return (
        <Button
          variant="map"
          size="map"
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75 print:hidden"
          onClick={(e) => updateIndex(e, 1)}>
          <LucideChevronRight className="h-5 w-5" />
        </Button>
      )
    }
  }

  const renderCount = () => {
    return (
      <span
        className="absolute bottom-2 left-2 z-10 select-none rounded-md bg-background p-2 text-foreground opacity-50 shadow-xl transition-opacity duration-200 group-hover:opacity-90 print:hidden"
        onClick={(e) => e.stopPropagation()}>
        {currentImageIndex + 1} / {(images || []).length}
      </span>
    )
  }

  const renderHeader = () => {
    return (
      <>
        {renderCloseButton()}
        {renderCount()}
      </>
    )
  }

  const renderButtons = () => {
    if ((images || []).length < 2) {
      return renderCloseButton()
    }
    return (
      <>
        {renderCloseButton()}
        {renderPrevButton()}
        {renderNextButton()}
      </>
    )
  }

  if (children) {
    return (
      <>
        <button type="button" onClick={() => setIsGalleryFullscreen(true)}>
          {children}
        </button>
        {/* TODO: listing.images[0].thumbUrl - verify image URL handling */}
        {(images || []).length > 0 && (
          <Lightbox
            className="absolute inset-0 bg-muted bg-opacity-75"
            isOpen={isGalleryFullscreen}
            onPrev={gotoPrevious}
            onNext={gotoNext}
            images={imagesList}
            currentIndex={currentImageIndex}
            onClose={() => {
              setIsGalleryFullscreen(false)
            }}
            renderPrevButton={renderPrevButton}
            renderNextButton={renderNextButton}
            renderHeader={renderHeader}
          />
        )}
      </>
    )
  }
  return (
    <div
      className={cn(
        'aspect-8/5 overflow-hidden bg-muted md:rounded-t-md',
        className
      )}>
      <div
        className={clsx(
          `group relative aspect-8/5 select-none md:rounded-t-md`,
          (images || []).length === 0 ? 'bg-muted' : 'bg-slate-900/75'
        )}>
        {(images || []).length === 0 && (
          <LucideImage className="absolute inset-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform text-foreground/75" />
        )}
        {(images || []).length > 0 && renderCount()}
        {renderButtons()}
        {(images || []).map((image, index) => {
          const isWatermarked = isWatermarkedImage(image)

          return (
            <div
              key={index}
              className={clsx(
                `absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center`,
                index !== currentImageIndex && 'hidden'
              )}>
              {isWatermarked ? (
                <WatermarkedImageDisplay />
              ) : (
                <img
                  loading="lazy"
                  className={clsx(
                    `aspect-8/5 select-none break-inside-avoid object-cover transition-opacity duration-200`,
                    !isEmbed && 'cursor-pointer',
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  )}
                  src={image.url}
                  onClick={(e) => {
                    if (isGalleryFullscreen && (images || []).length > 1) {
                      updateIndex(e, 1)
                    } else {
                      e.stopPropagation()
                      if (!isEmbed) {
                        setIsGalleryFullscreen(true)
                      }
                    }
                  }}
                />
              )}
            </div>
          )
        })}
        {(images || []).length > 0 && (
          <Lightbox
            className="absolute inset-0 bg-muted bg-opacity-75"
            isOpen={isGalleryFullscreen}
            onPrev={gotoPrevious}
            onNext={gotoNext}
            images={imagesList}
            currentIndex={currentImageIndex}
            onClose={() => {
              setIsGalleryFullscreen(false)
            }}
            renderPrevButton={renderPrevButton}
            renderNextButton={renderNextButton}
            renderHeader={renderHeader}
          />
        )}
      </div>
    </div>
  )
}
